import { useCallback, useEffect, useState } from 'react';
import { DownloadSimple, FilePdf, FileText, Sparkle } from '@phosphor-icons/react';
import { useCompany, useWorkspace } from '../App';
import { api, ApiError } from '../lib/api';
import { faNumber, formatBytes, jalaliDate, statusLabel } from '../lib/format';
import { Card, EmptyState, InlineNotice, LoadingState, PageHeader, StatusBadge } from '../components/Ui';
import { CustomSelect } from '../components/SmartForm';
import type { ReportDetail, ReportSummary } from '../types';

type DetailLevel = 'executive' | 'standard' | 'full';

export function ReportsPage() {
  const { fiscalYear, periodKey, bootstrap, can } = useWorkspace();
  const company = useCompany();
  const kinds = bootstrap.capabilities.reportKinds;
  const [reports, setReports] = useState<ReportSummary[]>([]);
  const [selected, setSelected] = useState<ReportDetail | null>(null);
  const [kind, setKind] = useState(() => {
    const requested = new URLSearchParams(window.location.search).get('kind');
    return requested && kinds.some((item) => item.key === requested)
      ? requested : (kinds[0]?.key ?? 'analytical');
  });
  const [detailLevel, setDetailLevel] = useState<DetailLevel>(bootstrap.settings.defaultReportDetail);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    const result = await api.reports(company.id, fiscalYear ? { fiscalYearId: fiscalYear.id } : {});
    setReports(result.reports);
    return result.reports;
  }, [company.id, fiscalYear]);

  useEffect(() => {
    setLoading(true);
    void load().catch((caught: Error) => setError(caught.message)).finally(() => setLoading(false));
  }, [load]);

  async function open(reportId: string) {
    try {
      setSelected((await api.report(company.id, reportId)).report);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'گزارش باز نشد.');
    }
  }

  async function generate() {
    if (!fiscalYear) return;
    setBusy(true);
    setError('');
    setNote('');
    try {
      const result = await api.generateReport(company.id, {
        kind, fiscalYearId: fiscalYear.id, periodKey, detailLevel, narrate: true,
      });
      setNote(result.report.noteFa);
      await load();
      await open(result.report.id);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'گزارش ساخته نشد.');
    } finally {
      setBusy(false);
    }
  }

  async function decide(reportId: string, decision: 'approved' | 'rejected') {
    try {
      await api.decideReport(company.id, reportId, decision);
      await load();
      await open(reportId);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'تصمیم ثبت نشد.');
    }
  }

  if (loading) return <LoadingState label="در حال بارگذاری گزارش‌ها" />;
  if (!fiscalYear) {
    return <EmptyState title="سال مالی انتخاب نشده است" description="گزارش همیشه برای یک سال مالی و یک دوره ساخته می‌شود." />;
  }

  const kindLabel = (key: string) => kinds.find((item) => item.key === key)?.labelFa ?? key;

  return (
    <>
      <PageHeader
        eyebrow="گزارش‌های تحلیلی"
        title="کتابخانه گزارش"
        description={`${company.name} — ${fiscalYear.labelFa}`}
      />

      {error && <InlineNotice tone="danger">{error}</InlineNotice>}
      {note && <InlineNotice tone="info">{note}</InlineNotice>}

      <Card>
        <div className="card-heading"><h2>ساخت گزارش تازه</h2></div>
        <div className="form-grid">
          <div className="field">
            <span className="smart-field__label">نوع گزارش</span>
            <CustomSelect
              ariaLabel="نوع گزارش"
              value={kind}
              onChange={setKind}
              options={kinds.map((item) => ({ value: item.key, label: item.labelFa, description: item.descriptionFa }))}
            />
          </div>
          <div className="field">
            <span className="smart-field__label">سطح جزئیات</span>
            <CustomSelect
              ariaLabel="سطح جزئیات"
              value={detailLevel}
              onChange={(value) => setDetailLevel(value as DetailLevel)}
              options={[
                { value: 'executive', label: 'خلاصه مدیریتی' },
                { value: 'standard', label: 'استاندارد' },
                { value: 'full', label: 'کامل' },
              ]}
            />
          </div>
        </div>
        {!bootstrap.capabilities.ai && (
          <p className="hint">
            تحلیل هوشمند پیکربندی نشده است؛ گزارش با جدول‌ها و ارقام ساخته می‌شود ولی متن تحلیلی ندارد.
          </p>
        )}
        <button className="button button--primary" onClick={generate} disabled={busy || !can('report.generate')}>
          <Sparkle size={16} weight="fill" /> {busy ? 'در حال ساخت…' : 'ساخت گزارش'}
        </button>
      </Card>

      <div className="reports-layout">
        <Card>
          <div className="card-heading">
            <h2>گزارش‌های ساخته‌شده</h2>
            <span className="data-badge">{faNumber(reports.length)}</span>
          </div>
          {reports.length === 0 ? (
            <p className="hint">هنوز گزارشی برای این سال مالی ساخته نشده است.</p>
          ) : (
            <div className="report-history">
              {reports.map((report) => (
                <button key={report.id} className={selected?.id === report.id ? 'active' : ''} onClick={() => open(report.id)}>
                  <strong>{kindLabel(report.kind)} <small>نسخه {faNumber(report.version)}</small></strong>
                  <small>{jalaliDate(report.createdAt)} — {statusLabel(report.status)}</small>
                </button>
              ))}
            </div>
          )}
        </Card>

        <Card className="report-workspace">
          {!selected ? (
            <EmptyState icon={<FileText size={26} />} title="گزارشی انتخاب نشده" description="یک گزارش را از فهرست باز کنید یا گزارش تازه‌ای بسازید." />
          ) : (
            <>
              <div className="report-cover">
                <div>
                  <h2>{selected.titleFa}</h2>
                  <p className="hint">
                    {statusLabel(selected.detailLevel)} — ساخته‌شده توسط {statusLabel(selected.generatedBy)} — {formatBytes(selected.byteSize)}
                  </p>
                </div>
                <StatusBadge status={selected.status} label={statusLabel(selected.status)} />
              </div>

              {selected.executiveSummaryFa
                ? <p className="report-summary">{selected.executiveSummaryFa}</p>
                : <p className="hint">این گزارش متن تحلیلی ندارد؛ فقط ارقام موتور محاسبات را نشان می‌دهد.</p>}

              {selected.sections.map((section) => (
                <section key={section.titleFa} className="report-section">
                  <h3>{section.titleFa}</h3>
                  {section.bodyFa && <p>{section.bodyFa}</p>}
                  {section.figures.length > 0 && (
                    <div className="data-table">
                      <table>
                        <thead><tr><th>عنوان</th><th>مقدار</th></tr></thead>
                        <tbody>
                          {section.figures.map((figure) => (
                            <tr key={figure.labelFa}>
                              <td>{figure.labelFa}</td>
                              <td className="statement-table__amount">{figure.value}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </section>
              ))}

              <div className="report-foot-actions">
                <a className="button button--primary button--small" href={api.reportExportUrl(company.id, selected.id, 'pdf')}>
                  <FilePdf size={15} /> خروجی PDF
                </a>
                <a className="button button--soft button--small" href={api.reportExportUrl(company.id, selected.id, 'xlsx')}>
                  <DownloadSimple size={15} /> خروجی اکسل
                </a>
                <a className="button button--soft button--small" href={api.reportExportUrl(company.id, selected.id, 'html')}>
                  <DownloadSimple size={15} /> خروجی HTML
                </a>
                <a className="button button--soft button--small" href={api.reportExportUrl(company.id, selected.id, 'md')}>
                  <DownloadSimple size={15} /> خروجی Markdown
                </a>
                {selected.status === 'review_required' && can('report.approve') && (
                  <>
                    <button className="button button--primary button--small" onClick={() => decide(selected.id, 'approved')}>تأیید گزارش</button>
                    <button className="button button--ghost button--small" onClick={() => decide(selected.id, 'rejected')}>رد گزارش</button>
                  </>
                )}
              </div>
            </>
          )}
        </Card>
      </div>
    </>
  );
}
